import { notifications } from "@mantine/notifications";
import i18n from "../i18n";
import { ExerciseRunResult, Permission } from "../types/user.types";
import { createRoles } from "./constants";

export const searchString = (source: string, searchString: string) => {
  const normalizedSource = source.trim().toLowerCase();
  return searchString
    .split(" ")
    .every((s: string) => normalizedSource.includes(s.toLowerCase()));
};

const courseTypeSuffixes = [
  // sv
  "Upplåst",
  "Fri",
  "Linjär",
  "Grund",
  // en
  "Linear",
  "Free",
  "Open",
  // fi
  "Vapaa",
  "Lineaarinen",
  // fr
  "Linéaire",
  "Lineaire",
  "Libre",
];

const courseTypeSuffixRegex = new RegExp(
  courseTypeSuffixes.map((s) => ` - ${s}`).join("|"),
  "gi",
);

export const trimCourseName = (name?: string) => {
  if (!name) return "";
  return name.replace(courseTypeSuffixRegex, "");
};

export const nameToInitials = (name: string): string =>
  name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

export const isEmptyOrOnlySpaces = (string: string): boolean =>
  string.replace(/\s+/g, "") === "";

export function permissionToReadable(permission: Permission): string {
  switch (permission.Code) {
    case "permissions:read":
      return "View user permissions";
    case "permissions:write":
      return "Change user permissions";
    case "cache:read":
      return "Read cache";
    case "cache:write":
      return "Write cache";
    case "simulators:write":
      return "Edit all simulators";
    case "lecture:read":
      return "See teacher training courses";
    case "lecture:write":
      return "Create teacher training courses";
    case "billing:readwrite":
      return "Manage billing";
    case "offlinelicense:readwrite":
      return "Manage offline licenses";
  }
  return permission.Code;
}

export function getCloudinaryVehicleUrl(
  id: string,
  width?: number,
  height?: number,
  aspectRatio?: string,
  customTransformation?: string,
) {
  const base = import.meta.env.VITE_CLOUDINARY_BASE ?? "";
  const breakPoint = "upload";
  const part1 = base.slice(0, base.indexOf(breakPoint) + breakPoint.length);
  const part2 = base.slice(
    base.indexOf(breakPoint) + breakPoint.length,
    base.length,
  );
  const ar = aspectRatio ?? "16:9";
  let transformation = width || height ? `/ar_${ar},c_fill,` : "";
  if (width) transformation += `w_${width}`;
  if (width && height) transformation += ",";
  if (height) transformation += `h_${height}`;
  if (customTransformation) transformation += `,${customTransformation}`;
  return `${part1}${transformation}${part2}/${id}`;
}

export function getCloudinaryExerciseUrl(
  id?: string,
  width?: number,
  height?: number,
  aspectRatio?: string,
  customTransformation?: string,
) {
  if (!id) return null;
  const base = import.meta.env.VITE_CLOUDINARY_BASE_EXERCISE ?? "";
  const breakPoint = "upload";
  const part1 = base.slice(0, base.indexOf(breakPoint) + breakPoint.length);
  const part2 = base.slice(
    base.indexOf(breakPoint) + breakPoint.length,
    base.length,
  );
  const ar = aspectRatio ?? "16:9";
  const improve = "e_improve:50,";
  let transformation =
    width || height ? `/ar_${ar},c_fill,${improve}` : improve;
  if (width) transformation += `w_${width}`;
  if (width && height) transformation += ",";
  if (height) transformation += `h_${height}`;
  if (customTransformation) transformation += `,${customTransformation}`;
  return `${part1}${transformation}${part2}/${id}`;
}

export function getCloudinaryLectureUrl(
  lectureUuid: string,
  imageId: string,
  width?: number,
  height?: number,
  aspectRatio?: string,
  customTransformation?: string,
) {
  const base = import.meta.env.VITE_CLOUDINARY_BASE_LECTURES ?? "";
  const breakPoint = "upload";
  const part1 = base.slice(0, base.indexOf(breakPoint) + breakPoint.length);
  const part2 = base.slice(
    base.indexOf(breakPoint) + breakPoint.length,
    base.length,
  );
  const ar = aspectRatio ?? "16:9";
  let transformation = `/ar_${ar},c_fill`;
  if (width) transformation += `,w_${width}`;
  if (width && height) transformation += ",";
  if (height) {
    if (width) transformation += `h_${height}`;
    else transformation += `,h_${height}`;
  }
  if (customTransformation) transformation += `,${customTransformation}`;
  return `${part1}${transformation}${part2}/${lectureUuid}/${imageId}`;
}

export const compareSemanticVersions = (a: string, b: string) => {
  const a1 = a.split(".");
  const b1 = b.split(".");
  const len = Math.min(a1.length, b1.length);
  for (let i = 0; i < len; i++) {
    const a2 = +a1[i] || 0;
    const b2 = +b1[i] || 0;
    if (a2 !== b2) {
      return a2 > b2 ? 1 : -1;
    }
  }
  return b1.length - a1.length;
};

export const trimDeactivatedName = (name?: string): string =>
  name?.replace(/-\@@-DEACTIVATED-\S*-\@@-/g, "") ?? "";
export const trimAnonymizedName = (name: string): string =>
  name.replace(/^ANONYMIZED_\d{4}-\d{2}-\d{2}_[a-zA-Z0-9-]+/g, "**********");

export function exerciseResultToText(result: ExerciseRunResult): string {
  switch (result) {
    case ExerciseRunResult.FailedPenaltyOverFailureThreshold:
      return i18n.t(
        "common.exerciseRunStatuses.failedPenaltyOverFailureThreshold",
      );
    case ExerciseRunResult.FailedToFollowInstructions:
      return i18n.t("common.exerciseRunStatuses.failedToFollowInstructions");
    case ExerciseRunResult.OutsideExerciseArea:
      return i18n.t("common.exerciseRunStatuses.outsideExerciseArea");
    case ExerciseRunResult.Timeout:
      return i18n.t("common.exerciseRunStatuses.timeout");
    case ExerciseRunResult.Aborted:
      return i18n.t("common.exerciseRunStatuses.aborted");
    case ExerciseRunResult.Failed:
      return i18n.t("common.exerciseRunStatuses.failed");
    default:
      return i18n.t("common.exerciseRunStatuses.completed");
  }
}

export function getRoleName(role: number) {
  const roles = createRoles({ includeAdmin: true });
  return roles.find((r) => r.value === role.toString())?.label;
}

export function downloadStringAsFile(
  text: string,
  nameWithFileExtension: string,
) {
  const blob = new Blob([text], { type: "text/plain" });

  const anchor = document.createElement("a");
  anchor.download = nameWithFileExtension;
  anchor.href = window.URL.createObjectURL(blob);
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();

  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(anchor.href);
}

export function openStringInNewTab(text: string) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  window.open(url, "_blank");
  window.URL.revokeObjectURL(url);
}

export function downloadFileFromBase64(base64Data: string, filename: string) {
  const blob = base64ToBlob(base64Data, "application/gzip");
  if (!blob) {
    notifications.show({
      color: "red",
      title: filename,
      message: "The file could not be decoded.",
    });
    return;
  }

  const anchor = document.createElement("a");
  anchor.download = filename;
  anchor.href = window.URL.createObjectURL(blob);
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();

  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(anchor.href);
}

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });

/** Decodes base64 gzip data to text. Returns null if it isn't valid base64 gzip. */
export async function gunzipBase64(base64Data: string): Promise<string | null> {
  const blob = base64ToBlob(base64Data, "application/gzip");
  if (!blob) return null;
  try {
    const stream = blob.stream().pipeThrough(new DecompressionStream("gzip"));
    return await new Response(stream).text();
  } catch {
    return null;
  }
}

function base64ToBlob(base64Data: string, type: string): Blob | null {
  let binaryString: string;
  try {
    binaryString = atob(base64Data);
  } catch {
    return null;
  }
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: type });
}

export function createSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function getServerName(): string | null {
  // Called at module top level — a missing env var must not throw here or the
  // whole bundle fails to load.
  const uri = import.meta.env.VITE_API_URI;
  if (typeof uri !== "string") return null;
  const match = uri.match(/^https:\/\/(.+)\.skillster\.se$/);
  return match?.[1] ?? null; // Servernamnet (admin, devel, devel2 etc)
}

export function stripHtmlTags(text: string = ""): string {
  return text.replace(/<[^>]*>/g, "");
}

export function isTruncated(element: HTMLParagraphElement | null) {
  if (element === null) return false;
  return element.offsetWidth < element.scrollWidth;
}

export function formatFileSize({
  bytes,
  decimals = 1,
}: {
  bytes: number;
  decimals?: number;
}) {
  if (bytes === 0) {
    return "0 Bytes";
  }

  const units = ["Bytes", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const k = 1000;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const dm = i < 2 ? 0 : decimals < 0 ? 0 : decimals;
  const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

  return `${formattedSize} ${units[i]}`;
}

export function truncateUuid(id: string, prefixLength = 8, postfixLength = 4) {
  if (id.length <= prefixLength + postfixLength) return id;
  return `${id.slice(0, prefixLength)}..${id.slice(-postfixLength)}`;
}

/**
 * The single spelling every email comparison goes through. The import checked
 * rows against each other case-sensitively while checking them against the
 * school with a lowercased compare, so "Kalle@x.se" and "kalle@x.se" passed as
 * two different users and collided first on the server.
 */
export const normalizeEmail = (email: string) => email.trim().toLowerCase();
